import { Button, Icon } from "@legal-500/legal500-design-system";
import { CmsImagePreview } from "../../../components/CMS/CmsImagePreview/CmsImagePreview";
import type { CmsBlock } from "../../../types/cms";
import { toCardGridContent, toIframeContent } from "./blockContent";
import type {
  AddressGridAddress,
  AddressGridContent,
  CardGridContent,
  CardModalGridContent,
  CardModalGridCard,
  IframeContent,
} from "./types";
import {
  SECTION_BLOCK_TYPES,
  SectionBlockPreview,
  SectionPreviewHeader,
} from "./SectionBlockPreview";
import styles from "./BlockPreview.module.css";

type CardPreviewProps<T> = {
  cards: T[];
  onEditCard?: (index: number) => void;
  onAddCard?: () => void;
  onDeleteCard?: (index: number) => void;
};

function CardGridPreview({
  content,
  onEditCard,
  onEditBlock,
}: {
  content: CardGridContent;
  onEditCard?: (index: number) => void;
  onEditBlock?: () => void;
}) {
  const { cards } = content;
  const sorted = [...cards].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className={styles.cardGrid}>
      <SectionPreviewHeader
        heading={content.heading}
        intro={content.intro}
        background={content.background}
        onEdit={onEditBlock}
      />
      {cards.length === 0 && <p>No cards yet.</p>}
      {sorted.map(card => {
        const originalIndex = cards.findIndex(c => c.id === card.id);
        return (
          <div key={card.id} className={styles.card}>
            <div className={styles.cardBody}>
              <div className={styles.cardTitle}>{card.title}</div>
              <div className={styles.cardText}>{card.text}</div>
            </div>
            {onEditCard && (
              <Button
                variant="outlined"
                color="default"
                aria-label={`Edit Card ${originalIndex + 1}: ${card.title || "Untitled"}`}
                onClick={() => onEditCard(originalIndex)}
              >
                Edit
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CardModalGridPreview({
  cards,
  onEditCard,
  onAddCard,
  onDeleteCard,
}: CardPreviewProps<CardModalGridCard>) {
  const sorted = [...cards].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className={styles.cardGrid}>
      {onAddCard && (
        <div className={styles.cardGridActions}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            aria-label="Add Card"
            onClick={onAddCard}
          >
            <Icon icon="plus" size="small" />
            Add Card
          </Button>
        </div>
      )}
      {cards.length === 0 && <p>No cards yet.</p>}
      {sorted.map(card => {
        const originalIndex = cards.findIndex(c => c.id === card.id);
        return (
          <div key={card.id} className={styles.personCard}>
            <CmsImagePreview
              key={card.image}
              value={card.image}
              alt={`${card.heading || "Card"} photo`}
              initial={card.heading?.charAt(0)?.toUpperCase() || "?"}
              size="small"
            />
            <div className={styles.personInfo}>
              <div className={styles.personName}>{card.heading}</div>
              <div className={styles.personRole}>{card.subheading}</div>
            </div>
            {onEditCard && (
              <div className={styles.personEditAction}>
                <Button
                  variant="outlined"
                  color="default"
                  aria-label={`Edit Card ${originalIndex + 1}: ${card.heading || "Untitled"}`}
                  onClick={() => onEditCard(originalIndex)}
                >
                  Edit
                </Button>
                {onDeleteCard && (
                  <Button
                    type="button"
                    variant="outlined"
                    color="error"
                    aria-label={`Delete Card ${originalIndex + 1}: ${card.heading || "Untitled"}`}
                    onClick={() => onDeleteCard(originalIndex)}
                  >
                    Delete Card
                  </Button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function IframePreview({ content, onEdit }: { content: IframeContent; onEdit?: () => void }) {
  return (
    <div className={styles.cardGrid}>
      <SectionPreviewHeader
        heading={content.heading}
        intro={content.intro}
        background={content.background}
        onEdit={onEdit}
      />
      <div className={styles.iframePreview}>
        <div className={styles.iframeIcon}>&#128506;</div>
        <div className={styles.cardBody}>
          <div className={styles.iframeTitle}>{content.title || "No title"}</div>
          <div className={styles.iframeSrc}>{content.src || "No source URL yet."}</div>
        </div>
      </div>
    </div>
  );
}

type AddressGridPreviewProps = {
  addresses: AddressGridAddress[];
  onEdit?: () => void;
};

function AddressGridPreview({ addresses, onEdit }: AddressGridPreviewProps) {
  const offices = addresses
    .filter(address => address.city)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <div className={styles.cardGrid}>
      {onEdit && (
        <div className={styles.cardGridActions}>
          <Button variant="outlined" color="default" onClick={onEdit}>
            Edit
          </Button>
        </div>
      )}
      {offices.length === 0 && <p>No offices yet.</p>}
      {offices.map((address, index) => (
        <div key={`${index}-${address.city}`} className={styles.card}>
          <div className={styles.cardBody}>
            <div className={styles.cardTitle}>{address.city}</div>
            <div className={styles.cardText}>{(address.lines ?? []).join(", ")}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FallbackPreview({ content }: { content: Record<string, unknown> | null }) {
  if (!content) return <div className={styles.empty}>No content</div>;
  const keys = Object.keys(content);
  return (
    <div className={styles.fallback}>
      {keys.map(key => (
        <span key={key} className={styles.fallbackKey}>
          {key}
        </span>
      ))}
    </div>
  );
}

function isCardModalGridContent(content: Record<string, unknown>): content is CardModalGridContent {
  return (
    Array.isArray(content.cards) && (content.cards.length === 0 || "heading" in content.cards[0])
  );
}

function isAddressGridContent(content: Record<string, unknown>): content is AddressGridContent {
  return Array.isArray(content.addresses);
}

type BlockPreviewProps = {
  block: CmsBlock;
  onEditCard?: (cardIndex: number) => void;
  onAddCard?: () => void;
  onDeleteCard?: (cardIndex: number) => void;
  onEditBlock?: () => void;
};

export default function BlockPreview({
  block,
  onEditCard,
  onEditBlock,
  onAddCard,
  onDeleteCard,
}: BlockPreviewProps) {
  const content = block.content;

  if (block.type === "block_card_grid") {
    return (
      <CardGridPreview
        content={toCardGridContent(content)}
        onEditCard={onEditCard}
        onEditBlock={onEditBlock}
      />
    );
  }

  if (block.type === "block_iframe") {
    return <IframePreview content={toIframeContent(content)} onEdit={onEditBlock} />;
  }

  if (SECTION_BLOCK_TYPES.includes(block.type)) {
    return <SectionBlockPreview type={block.type} content={content} onEdit={onEditBlock} />;
  }

  if (block.type === "block_card_modal_grid" && (!content || content.cards == null)) {
    return (
      <CardModalGridPreview
        cards={[]}
        onEditCard={onEditCard}
        onAddCard={onAddCard}
        onDeleteCard={onDeleteCard}
      />
    );
  }

  if (!content) return <div className={styles.empty}>No content</div>;

  if (block.type === "block_card_modal_grid" && isCardModalGridContent(content)) {
    return (
      <CardModalGridPreview
        cards={content.cards}
        onEditCard={onEditCard}
        onAddCard={onAddCard}
        onDeleteCard={onDeleteCard}
      />
    );
  }

  if (block.type === "block_address_grid" && isAddressGridContent(content)) {
    return <AddressGridPreview addresses={content.addresses} onEdit={onEditBlock} />;
  }

  return <FallbackPreview content={content} />;
}
